<template>
    <section class="tickets">
        <div class="level m-t-md m-b-md">
            <div class="level-left">
                <div class="level-item">
                    <span class="title">Edit #{{ $route.params.id | makeIDSixDigits }}</span>
                    <span
                        class="tag is-danger is-medium m-l-sm"
                        v-if="data.is_escalated == 1 "
                    >ESCALATED</span>
                </div>
                <div class="level-item" v-if="(isRole('gatekeeper') || isRole('serviceperson'))">
                    <b-button
                        :type="`${data.on_hold == 0 ? 'is-dark' : 'is-warning'} m-r-sm`"
                        outlined
                        @click.prevent="data.on_hold = data.on_hold ? 0 : 1, save(false)"
                    >
                        ON HOLD
                    </b-button>
                </div>
            </div>
            <div class="level-right">
                <div class="level-item">
                    <b-button
                        type="is-danger m-r-sm"
                        outlined
                        v-if="data.is_escalated == 0 && isRole('gatekeeper')"
                        @click.prevent="data.is_escalated = 1, save(false)"
                    >ESCALATE</b-button>
                    <b-button
                        type="is-info"
                        outlined
                        v-if="data.is_escalated == 1 && data.is_escalated_to_developer == 0 && ( isRole('gatekeeper') || isRole('serviceperson'))"
                        @click.prevent="data.is_escalated = 0, save(false)"
                    >NEUTRALIZE</b-button>
                </div>
            </div>
            <div class="level-right">
                <div class="level-item">
                    <b-button
                        type="is-danger m-r-sm"
                        outlined
                        v-if="data.is_escalated_to_developer == 0 && data.is_escalated && isRole('serviceperson')"
                        @click.prevent="data.is_escalated_to_developer = 1, save(false)"
                    >ESCALATE TO DEVELOPER</b-button>
                    <b-button
                        type="is-info"
                        outlined
                        v-if="data.is_escalated_to_developer == 1 && (isRole('developer') || isRole('serviceperson'))"
                        @click.prevent="data.is_escalated_to_developer = 0, save(false)"
                    >NEUTRALIZE</b-button>
                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <div class="card">
                    <div class="card-content">
                        <div class="columns">
                            <div class="column is-half">
                                <b-field horizontal>
                                    <template #label>
                                        Status <span class="has-text-danger">*</span>
                                    </template>
                                    <b-select v-model="data.status" id="select-status" expanded :disabled="!(isRole('developer') || isRole('serviceperson') || isRole('gatekeeper' || isRole('superadmin')))">
                                        <option :value="null">Please Select</option>
                                        <option
                                            v-for="(status, index) in statuses"
                                            :key="index"
                                            :value="status"
                                            :disabled="isStatusOptionDisabled(status)"
                                        >{{ status.toUpperCase() }}</option>
                                    </b-select>
                                </b-field>


                                <b-field horizontal>
                                    <template #label>
                                        Type <span class="has-text-danger">*</span>
                                    </template>
                                    <b-select v-model="data.type" id="select-type" expanded :disabled="canEdit">
                                        <option :value="null">Please Select</option>
                                        <option
                                            v-for="(type, index) in types"
                                            :key="index"
                                            :value="type"
                                        >{{ type }}</option>
                                    </b-select>
                                </b-field>

                                <b-field v-show="apps.length>1" horizontal>
                                    <template #label>
                                        App <span class="has-text-danger">*</span>
                                    </template>
                                    <app-filter v-model="data.app_id" expanded :preSelect="data.app_id"></app-filter>
                                </b-field>

                                <b-field horizontal>
                                    <template #label>
                                        Priority <span class="has-text-danger">*</span>
                                    </template>
                                    <b-select v-model="data.priority" id="select-priority" expanded :disabled="canEdit">
                                        <option :value="null">Please Select</option>
                                        <option
                                            v-for="(priority, index) in priorities"
                                            :key="index"
                                            :value="priority"
                                        >{{ priority }}</option>
                                    </b-select>
                                </b-field>

                                <b-field horizontal>
                                    <template #label>
                                        Details <span class="has-text-danger">*</span>
                                    </template>
                                    <b-input type="textarea" v-model="data.details" id="input-details" :disabled="canEdit"></b-input>
                                </b-field>

                                <b-field label="Location" horizontal>
                                    <b-field>
                                        <b-checkbox-button type="is-dark" v-model="selectedEnvironment" native-value="production">
                                            Production
                                        </b-checkbox-button>
                                        <b-checkbox-button type="is-dark" v-model="selectedEnvironment" native-value="test">
                                            Test
                                        </b-checkbox-button>
                                    </b-field>
                                </b-field>

                                <b-field label="Reference" horizontal>
                                    <b-input v-model="data.third_party_code" id="input-reference" :disabled="canEdit"></b-input>
                                </b-field>

                                <b-field label="Tags" horizontal>
                                    <b-taginput
                                        v-model="tagsSelected"
                                        ellipsis
                                        :data="filteredTags"
                                        placeholder="Add a tag"
                                        aria-close-label="Delete this tag"
                                        allow-new
                                        field="attributes.value"
                                        type="is-dark"
                                        autocomplete
                                        open-on-focus
                                        :remove-on-keys="['Backspace']"
                                        :confirm-keys='[",", "Tab", "Enter"]'
                                        @typing="getFilteredTags"
                                        :allow-duplicates="false"
                                        ref="tags"
                                        :disabled="canEdit"
                                        >
                                        <template #selected="props">
                                            <b-tag
                                                v-for="(tag, index) in props.tags"
                                                :key="index"
                                                type="is-dark"
                                                rounded
                                                :tabstop="false"
                                                ellipsis
                                                :closable="isClosable(tag)"
                                                @close="$refs.tags.removeTag(index, $event)"
                                                >
                                                {{tag.attributes ? tag.attributes.value : tag}}

                                            </b-tag>
                                        </template>
                                    </b-taginput>
                                </b-field>

                                <!-- <b-field label="Rank" v-if="isNotUser()" horizontal>
                                    <b-field grouped>
                                        <b-numberinput :editable="false" type="is-dark" v-model="data.rank" min="0" step="10" exponential controls-position="compact" expanded></b-numberinput>
                                    </b-field>
                                </b-field> -->

                                <b-field label="Point" v-if="isRole('serviceperson') && data.point_id" horizontal>
                                    {{data.point_id}}
                                </b-field>
                                <b-field label="Developer" v-if="data.is_escalated_to_developer && isNotUser()" horizontal>
                                    <b-select v-model="data.developer_id" expanded>
                                        <option :value="null">Please Select</option>
                                        <option :value="developer.id" v-for="(developer,key) in developers" :key="key">{{developer.attributes.display_name}}</option>
                                    </b-select>
                                </b-field>
                                <div class="card" v-if="data.is_escalated_to_developer && isNotUser()">
                                    <div class="card-header has-background-light">
                                        <p class="card-header-title">Release</p>
                                    </div>
                                    <div class="card-content">
                                        <label class="label">Release name</label>
                                        <div class="field is-grouped">
                                            <b-select v-model="data.release_id" expanded :disabled="!(auth.permissions.includes('can_manage_release') || isRole('developer') || isRole('serviceperson'))">
                                                <option :value="null">Please Select</option>
                                                <option
                                                    v-for="release in releases"
                                                    :key="release.id"
                                                    :value="release.id"
                                                >{{ release.attributes.name }}</option>
                                            </b-select>
                                            <p class="control" v-if="data.release_id">
                                                <b-button @click.prevent="viewRelease">view</b-button>
                                            </p>
                                        </div>
                                        <b-field label="Release Details" :disabled="!auth.permissions.includes('can_manage_release')">
                                            <b-input type="textarea" v-model="data.release_notes"></b-input>
                                        </b-field>

                                        <label class="label">Parent Ticket</label>
                                        <div class="field is-grouped">
                                            <b-select v-model="data.ticket_parent_id" expanded :disabled="!auth.permissions.includes('can_manage_release')">
                                                <option :value="null">No Parent Ticket</option>
                                                <option
                                                    v-for="parent in parentTickets"
                                                    :key="parent.id"
                                                    :value="parent.id"
                                                >#{{ parent.id | makeIDSixDigits}}</option>
                                            </b-select>
                                            <p class="control" v-if="data.ticket_parent_id">
                                                <b-button @click.prevent="viewParent">view</b-button>
                                            </p>
                                        </div>

                                        <b-field label="Related Tickets">
                                            <div class="list">
                                                <a
                                                    class="list-item"
                                                    v-for="related in relatedTicketList"
                                                    :key="related.id"
                                                    @click.prevent="goToChildrenTicket(related.id)"
                                                >#{{ related.id | makeIDSixDigits }}</a>
                                            </div>
                                        </b-field>
                                    </div>
                                </div>
                                <section class="m-t-md">
                                    <b-button
                                        type="is-default m-r-sm"
                                        @click.prevent="goBack()"
                                    >Cancel</b-button>
                                    <b-button id="btn-save" type="is-success m-r-sm" @click.prevent="save">Save</b-button>
                                </section>
                            </div>
                            <div class="column is-half">
                                <section class="m-b-md">
                                    <files :ticketFiles="files"></files>
                                </section>

                                <comments :type_id="$route.params.id" :model.sync="data" :type="`tickets`" v-if="data.created_at"/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
<script>
import Vue from "vue";
import comments from "@/pages/_includes/comments";
import files from "./_includes/files";
import { setTimeout } from "timers";
import appFilter from '../../../../components/AppFilter.vue';
import _ from 'lodash/lodash';

export default {
    data() {
        return {
            files: [],
            releases: [],
            parentTickets: [],
            relatedTicketList: [],
            apps: [],
            statuses: [
                "unresolved",
                "acknowledged",
                "work in progress",
                "queued",
                "developing",
                "ready for QA testing",
                "ready for UA testing",
                "ready for release",
                "released",
                "resolved",
                "void"
            ],
            statusAllowedForGateKeeper:[
                "unresolved",
                "acknowledged",
                "work in progress",
                "queued",
                "developing",
                "ready for UA testing",
                "ready for release",
                "released",
                "resolved",
                "void"
            ],
            types: ["Bug Report", "Feature Suggestion", "Service Request"],
            priorities: ["Critical", "Urgent", "Routine"],
            data: {
                type: null,
                status: null,
                priority: null,
                details: null,
                release_id: null,
                release_notes: null,
                ticket_parent_id: null,
                developer_id:null,
                app_id: null,
                is_escalated: 0,
                is_escalated_to_developer: 0,
                point_id:null,
                third_party_code: null,
                tags:[],
                rank:null,
                environment: null,
                on_hold: 0,
            },
            commentData: {
                data: null,
                data_type: "text",
                scope: null
            },
            developers:[],
            filterAppText:'',
            tags:[],
            filteredTags:[],
            tagsSelected:[],
            createdBy:null,
            selectedEnvironment:[]
        };
    },
    components: {
        comments,
        files,
        appFilter
    },
    watch: {
        "data.release_id"(id) {
            if (id) {
                this.getParentTicketList();
            }
        },
        "data.app_id"(id) {
            if(id) {
                this.getReleases()
            }
        },
        "data.rank"(newValue, oldValue){
            if(oldValue!=null){
                this.data.rank = ((newValue/10)*10) - newValue%10
            }
        },
        tagsSelected(newValue){
            this.data.tags = []
            newValue.forEach((element)=>{
                if(typeof element === 'object'){
                    let value = element.attributes ? element.attributes.value : element.value
                    this.data.tags.push({id:element.id,value:value,createdBy:element.pivot ? element.pivot.created_by : this.user.id})
                }else{
                    this.data.tags.push({value:element})
                }
            })
        },
        selectedEnvironment(newValue,oldValue){
            if(newValue.length<=1){
                this.data.environment = newValue[0] ? newValue[0] : null
            }else{
                this.selectedEnvironment = _.difference(newValue,oldValue)
            }
        }
    },
    computed: {
        auth: {
            get() {
                return this.$store.state.auth;
            }
        },
        loading: {
            get() {
                return this.$store.state.loading;
            },
            set(value) {
                this.$store.commit("SET_LOADING", value);
            }
        },
        canEdit(){
            let isLoggedInTheCreator = (parseInt(this.createdBy) === parseInt(this.user.id));

            if(this.isNotUser() && !this.isRole('gatekeeper')){
                return false
            }else if(this.data.is_escalated && (this.isRole('gatekeeper') || isLoggedInTheCreator)){
                return true
            }else if(this.isRole('gatekeeper') || isLoggedInTheCreator){
                return false
            }
            return true
        },
        user(){
            return this.$store.state.user
        }
    },
    async mounted() {
        await this.getTags();
        await this.getTicket();
        await this.getReleases();
        this.getRelatedTicketList(this.$route.params.id);
        this.getAppList();
        if(this.data.is_escalated_to_developer) {
            this.getDevelopers();
        }
    },
    methods: {
        async getTicket() {
            this.loading = true;
            let params = "?includes=files";
            // Get Ticket Data
            const tickets = await Vue.$ticketService.getTicket(
                this.$route.params.id,
                params
            ).catch(err=>{
                if(err.response.status===403){
                    this.$router.push({name:'tickets'})
                }
            });

            this.data.type = tickets.data.attributes.type;
            this.data.status = tickets.data.attributes.status;
            this.data.priority = tickets.data.attributes.priority;
            this.data.details = tickets.data.attributes.details;
            this.data.release_id = tickets.data.attributes.release_id;
            this.data.release_notes = tickets.data.attributes.release_notes;
            this.data.ticket_parent_id = tickets.data.attributes.ticket_parent_id;
            this.data.is_escalated = tickets.data.attributes.is_escalated;
            this.data.is_escalated_to_developer = tickets.data.attributes.is_escalated_to_developer;
            this.data.app_id = tickets.data.attributes.app_id;
            this.data.point_id = tickets.data.attributes.point_id;
            this.data.third_party_code = tickets.data.attributes.third_party_code;
            this.data.rank = tickets.data.attributes.rank === null ? 0 : tickets.data.attributes.rank;
            this.filterAppText = this.data.app_id
            this.data.developer_id = tickets.data.attributes.developer_id ? tickets.data.attributes.developer_id.id : null;
            this.tagsSelected = tickets.data.attributes.tags
            this.createdBy = tickets.data.attributes.created_by.id;
            this.selectedEnvironment = [tickets.data.attributes.environment]

            this.data.on_hold = tickets.data.attributes.on_hold;

            this.data.created_at = tickets.data.attributes.created_at

            if (tickets.included) {
                this.files = tickets.included.filter(include => {
                    return (include.type == "files");
                });
            }
            this.loading = false;
        },

        getDevelopers() {
            Vue.$userService.userList('?roles=developer').then((response)=>{
                this.developers = response.data
            })
        },

        save(isReload) {
            this.loading = true;
            Vue.$ticketService
                .updateTicket(this.$route.params.id, this.data)
                .then(() => {
                    this.$buefy.toast.open({
                        message:"Ticket updated!",
                        type:'is-success',
                        queue:false
                    });
                    this.$store.commit("SET_ERRORS", null);
                    this.loading = false;
                    if(isReload) {
                        this.$router.push({
                            name: "tickets"
                        });
                    }else{
                        if(this.data.is_escalated_to_developer) {
                            this.getDevelopers();
                        }
                    }
                }).catch(()=>{
                    this.getTicket()
                    this.loading = false;
                })
        },

        getReleases() {
            let params = `?app_id=${this.data.app_id}`
            if(!this.data.release_id){
                params += `&status=pending`;
            }
            Vue.$releaseService.releaseList(params).then(response => {
                this.releases = response.data;
                if(!this.releases.length) {
                    this.data.release_id = null
                }
            });
        },

        getParentTicketList() {
            let params = `?release_id=${this.data.release_id}`;
            Vue.$ticketService.getParentTicketList(params).then(response => {
                response.data.forEach(parentTicket => {
                    if (parentTicket.id != this.$route.params.id) {
                        this.parentTickets.push(parentTicket);
                    }
                });
            });
        },

        getRelatedTicketList(id) {
            Vue.$ticketService.getRelatedTicketList(id).then(response => {
                this.relatedTicketList = response.data;
            });
        },

        goBack() {
            this.$store.commit("SET_ERRORS", null);
            this.$router.push({
                name: "tickets"
            });
        },

        getAppList() {
            // Vue.$authService.getAppList().then(apps => {
            //     this.apps = apps;
            // })
            // .catch(err => {
            // });
            Vue.$appService.appList().then((response)=>{
                this.apps = response
            });
        },

        goToChildrenTicket(id) {
            this.$router.push({
                name: "tickets.edit",
                params: {
                    id: id
                }
            });
            setTimeout(() => {
                this.getReleases();
                this.getTicket();
                this.getRelatedTicketList(this.$route.params.id);
            }, 500);
        },
        viewParent() {
            this.$router.push({
                name: "tickets.edit",
                params: {
                    id: this.data.ticket_parent_id
                }
            });
            setTimeout(() => {
                this.getReleases();
                this.getTicket();
                this.getRelatedTicketList(this.$route.params.id);
            }, 500);
        },
        viewRelease() {
            this.$router.push({
                name: "releases.edit",
                params: {
                    id: this.data.release_id
                }
            });
        },
        getTags(){
            Vue.$tagService.getList('?filter=user_tag').then((response)=>{
                this.tags=response.data
            })
        },
        getFilteredTags(text){
            this.filteredTags = this.tags.filter(element=>{
                return element.attributes.value.includes(text)
            })
        },
        isClosable(tag){
            if(typeof tag.pivot !== 'undefined' &&tag.attributes.type.includes('[SYSTEM]')){
                if(this.isRole('superadmin')){
                    return true
                }
                return false;
            }else if(typeof tag.pivot !== 'undefined'){
                if(this.isNotUser() && !tag.attributes.type.includes('[SYSTEM]')){
                    return true
                }else if(tag.pivot && parseInt(tag.pivot.created_by) === parseInt(this.user.id)){
                    return true
                }
            }else if(typeof tag.pivot === 'undefined'){
                return true
            }
            return false
        },
        isStatusOptionDisabled(status) {
            if (this.isRole("gatekeeper") && !this.isRole("superadmin")) {
                //return !statusAllowedForGateKeeper.includes(status);
                return ![
                    "unresolved",
                    "acknowledged",
                    "work in progress",
                    "queued",
                    "developing",
                    "ready for UA testing",
                    "ready for release",
                    "released",
                    "resolved",
                    "void"
                ].includes(status);
            }
            // if (this.isRole("developer") && this.data.developer_id == this.auth.user.id && !this.isRole("superadmin")) {
            if (this.isRole("developer") && !this.isRole("superadmin")) {
                return ![
                    "developing",
                    "ready for QA testing"
                ].includes(status);
            }
            return false;
        }
    }
};
</script>